import { useEffect, useState } from 'react';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useServices } from '@/ui/services/ServicesContext';
import styles from './UpdatePrompt.module.css';

/**
 * "Có bản mới rồi. Lúc nào rảnh thì cập nhật nhé." (ux-screens §8): the new version waits until the player taps. The
 * save is flushed first; IndexedDB is never touched by the update.
 */
export function UpdatePrompt() {
  const { t } = useI18n();
  const { saves } = useServices();
  const [update, setUpdate] = useState<((reload?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    if (!__FEATURE_PWA__ || import.meta.env.DEV) return;
    let cancelled = false;
    let poll: ReturnType<typeof setInterval> | undefined;
    let registration: ServiceWorkerRegistration | undefined;
    const check = () => {
      if (document.visibilityState === 'visible' && navigator.onLine)
        void registration?.update().catch(() => {
          /* Retry when back online or on the next poll. */
        });
    };
    window.addEventListener('focus', check);
    window.addEventListener('online', check);
    void import('virtual:pwa-register').then(({ registerSW }) => {
      if (cancelled) return;
      const updateSW = registerSW({
        onRegisteredSW: (_url, registered) => {
          if (cancelled) return;
          registration = registered;
          // Long-running sessions discover releases without a reload or a lost save.
          poll = setInterval(check, 5 * 60_000);
        },
        onNeedRefresh: () => {
          setUpdate(() => updateSW);
        },
      });
    });
    return () => {
      cancelled = true;
      clearInterval(poll);
      window.removeEventListener('focus', check);
      window.removeEventListener('online', check);
    };
  }, []);

  if (!update) return null;
  return (
    <div className={styles.toast} role="status">
      <span>{t('ui.update.available')}</span>
      <button
        type="button"
        className={styles.button}
        onClick={() => {
          void saves.flush().then(() => update(true));
        }}
      >
        {t('ui.update.now')}
      </button>
      <button
        type="button"
        className={styles.later}
        onClick={() => {
          setUpdate(null);
        }}
      >
        {t('ui.update.later')}
      </button>
    </div>
  );
}
